import React, { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDimensions } from '../../../../hooks/useDimensions';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { topNavOffsetClassesGetter } from '../../../../helpers/topNavOffsetClasses';
import classNames from 'classnames';
import classes from '../PostSchedule.module.scss';
import { Calendar } from '../../../../components/containers/Calendar';
import {
  PostSource,
  PostTiming,
  StartOfWeekFormatSetting,
  TimeFormatSetting,
  TimezoneSetting
} from '../../../../models/graphql';
import {
  CalendarDataType,
  CalendarHeaderNames,
  CellTemplateProps,
  QueueCellDataType
} from '../../../../models/calendar';
import { PostScheduleFilterMenu } from '../PostScheduleFilterMenu';
import { FluidContentLayoutView } from '../../../../components/containers/FluidContentLayout/FluidContentLayout.view';
import { CardOrientationEnum } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import { PostScheduleHelpers } from '../postScheduleHelpers';
import { BasePostCard } from '../../../../components/cards/BasePostCard';
import { PostActionType, PostScheduleWeekContext, PostScheduleWeekProvider } from './PostScheduleWeekProvider';
import { DetailedStatsModal } from '../DetailedStatsModal';
import { ContainerListItem } from 'components/actions/ListItem/ListItem.view';
import { add, addHours, differenceInMinutes, eachDayOfInterval, format, getMinutes, isBefore } from 'date-fns';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import { PostEditorContextEnum } from '../../../../models/post-editor';
import { AppContext } from '../../../../providers/AppProvider';
import { convertTimezoneEnumToName, mapDayTimePositionToDateString } from '../../../../helpers/timeFormatter';
import { useSettings } from '../../../../hooks/useSettings';
import { QueueCountContext } from '../../../../providers/QueueCountProvider';
import { CalendarCellActionLabel } from '../../../../components/actions/CalendarCellActionLabel';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { CSSTransition } from 'react-transition-group';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { TimezoneNames } from '../../../../constants/timezones';
import { PostScheduleWeekControllerProps } from './PostScheduleWeek.controller';
import { dateRoundTo } from '../../../../helpers/map-timezone';
import { DateStyle, FormatString } from 'hooks/useDateString';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { useOpenPostEditor } from '../../../../hooks/useOpenPostEditor';
import { ContainerListItemExtended } from '../../../../components/actions/ButtonMenu/ButtonMenu.view';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

type PostPositionsData = {
  first: number;
  last: number;
  post: boolean;
};

const CalendarCellTemplate = (props: CellTemplateProps<QueueCellDataType>): JSX.Element => {
  const { selectedWorkspace } = useSelectedOrganization();
  const tz: TimezoneNames =
    convertTimezoneEnumToName(selectedWorkspace?.timezone || TimezoneSetting.UTC) || TimezoneNames.UTC;
  const [_, setShouldCalculate] = useState<boolean>(false);
  const { row, position, timeFormat } = props;
  const { startDate, deletePostAction, editPostAction, newPostAction, duplicatePostAction } =
    useContext(PostScheduleWeekContext);
  const timeNow = new Date();
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;

  const [hour, day] = position;
  const isEmptyRow = row === undefined;

  if (day === 0 && hour > 0 && isEmptyRow) {
    const formatString =
      timeFormat === TimeFormatSetting.HOUR_12
        ? FormatString[DateStyle.TIME_ONLY].twelveHoursWithoutMinutes
        : FormatString[DateStyle.TIME_ONLY].HOUR_24;
    const date = timeNow.setHours(hour, 0, 0, 0);
    return (
      <td key={`${hour}-${day}-table-cell`} className={classes.AxisLabelCell}>
        <div className={classes.AxisLabel}>{format(date, formatString)}</div>
      </td>
    );
  }

  const zonedStartInBrowser = zonedTimeToUtc(startDate, tz);
  const zonedOffsetInBrowser = add(zonedStartInBrowser, { days: day - 1, hours: hour }); // we add day - 1 because 1st day has already value of 1, since 0 is reserved for labels column
  const zonedInZone = utcToZonedTime(zonedOffsetInBrowser, tz); // field time
  const currentInZone = utcToZonedTime(timeNow, tz); //

  const baseCellClasses = classNames({
    [classes.DisabledCell]: day !== 0 && startDate && isBefore(addHours(zonedInZone, 1), currentInZone)
  });
  const droppableCellClasses = classNames(classes.DroppableTableCell, baseCellClasses);

  if (isEmptyRow) {
    return <td key={`${hour}-${day}-table-cell`} className={baseCellClasses} />;
  }

  const onClickCell = () => {
    const differenceInMins = differenceInMinutes(zonedInZone, currentInZone);
    const currentDate = dateRoundTo(utcToZonedTime(new Date(), tz));
    let currentMinutes = 0;
    if (differenceInMins < 0) {
      currentMinutes = getMinutes(currentDate);
    }
    newPostAction(day, hour, currentMinutes);
  };

  return (
    <td
      key={`${hour}-${day}-table-cell`}
      className={droppableCellClasses}
      // We manage mouse over to force the Calendar Cell Action Label to calculate the cell height at that state
      onMouseOver={() => setShouldCalculate(old => (!old ? true : old))}
      onMouseOut={() => setShouldCalculate(old => (old ? false : old))}>
      <CalendarCellActionLabel labelText={'Add post'} clickHandler={onClickCell} />
      {row?.map((item, index) => {
        const menuListItems: Array<ContainerListItem | ContainerListItemExtended> = [
          { label: 'Duplicate', action: () => duplicatePostAction(item, 'duplicate') },
          { label: 'Edit', action: () => editPostAction(item.id) },
          {
            label: 'Trash',
            action: () => deletePostAction(item.id),
            ...(showOnboarding && disableDelete && item.onboardingContent
              ? { disabled: true, disabledTooltipText: 'You’ll be able to delete these demo posts later.' }
              : {})
          }
        ];

        return (
          <BasePostCard
            context={PostScheduleViewContext.WEEK}
            index={index}
            isDraggable={false}
            hideCheckbox={true}
            key={`${item.id}-${index}`}
            orientation={CardOrientationEnum.CALENDAR}
            post={item}
            menuListItems={menuListItems}
          />
        );
      })}
    </td>
  );
};

export type PostScheduleWeekViewProps = PostScheduleWeekControllerProps & {
  leftColRef: RefObject<HTMLDivElement>;
  getPostScheduleWeekPostItems: () => Promise<void>;
};

export const PostScheduleWeekView = (props: PostScheduleWeekViewProps): JSX.Element => {
  const { leftColRef, wsTz } = props;
  const { hasScrollbar: hasLeftScrollbar } = useDimensions(leftColRef);
  const { setPostEditorContext, setSuspendedModalOpen } = useContext(AppContext);
  const { state: topNavState } = useContext(TopNavContext);
  const { openNewPost } = useOpenPostEditor();
  const { selectedOrgSettings } = useSettings();
  const timeFormat = selectedOrgSettings?.timeFormat ?? TimeFormatSetting.HOUR_24;
  const startDayOfWeek = selectedOrgSettings?.startOfWeekFormat || StartOfWeekFormatSetting.SUNDAY;
  const topNavOffsetContext = useContext(TopNavContext);
  const { loadingInitial } = useContext(PostScheduleContext);
  const { queueCounts } = useContext(QueueCountContext);
  const [showMore, setShowMore] = useState<boolean>(false);
  const showMoreRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const postPositionsRef = useRef<PostPositionsData>({ first: -1, last: -1, post: false });

  const topNavClasses = topNavOffsetClassesGetter(topNavOffsetContext.state.visibility, 0);
  const leftColumnWrapperClass = classNames(classes.LeftSideWrapper, 'cell', 'show-for-xxlarge', topNavClasses.vheight);

  const rightColumnWrapperClass = classNames(topNavClasses.vheight, classes.RightSideColumn);
  const leftColumnClass = classNames(classes.LeftSideColumn, hasLeftScrollbar ? classes.LeftSideColumnScroller : null);

  const renderCallback = useCallback((elem: HTMLTableElement | null) => {
    tableRef.current = elem;
  }, []);

  const { startDate, endDate } = props;
  const headerNames = useMemo((): CalendarHeaderNames => {
    const datesRange = eachDayOfInterval({ start: startDate, end: endDate });
    const formattedDates = datesRange.map(date => format(date, 'E, MMM d'));
    formattedDates.unshift(''); // The first position must be empty
    return formattedDates as CalendarHeaderNames;
  }, [endDate, startDate]);

  const { scheduleItems } = props;
  const posts: CalendarDataType<QueueCellDataType> = useMemo(() => {
    const startOfWeekFormatSettings = selectedOrgSettings?.startOfWeekFormat || StartOfWeekFormatSetting.SUNDAY;
    return PostScheduleHelpers.transformPostData(scheduleItems, startOfWeekFormatSettings, wsTz);
  }, [scheduleItems, selectedOrgSettings?.startOfWeekFormat, wsTz]);
  const { isTemporallySuspended } = useSubscriptionStatus();

  const providerProps: PostActionType = {
    startDate,
    duplicatePostAction: props.duplicateOrReschedulePost,
    deletePostAction: props.deleteSinglePost,
    showDetailsAction: props.showDetailsModalHandler,
    editPostAction: (postId: string) => {
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      setPostEditorContext({
        type: PostEditorContextEnum.edit,
        postId: postId,
        onClose: topNavState.newPostCloseCallback
      });
    },
    newPostAction: (day: number, hour: number, minutes: number) => {
      openNewPost({
        source: PostSource.QUEUE_WEEK_SLOT_EMPTY,
        onClose: topNavState.newPostCloseCallback,
        groupTiming: {
          postTiming: PostTiming.SCHEDULE,
          date: [mapDayTimePositionToDateString(day, hour, minutes, startDayOfWeek, startDate)]
        }
      });
    }
  };

  const { queueModals } = props;
  useEffect(() => {
    //scroll to the first visible post
    if (tableRef.current && !loadingInitial && startDate && endDate) {
      const tableElem = tableRef.current;
      const { rowIndex, post } = PostScheduleHelpers.getFirstPost(posts);
      const { rowIndex: lastIndex } = PostScheduleHelpers.getLastPost(posts);
      postPositionsRef.current = { ...postPositionsRef.current, first: rowIndex, last: lastIndex, post };
      if (post) {
        setTimeout(() => {
          // trigger scroll to check for the label
          const parentHolder = tableElem.parentElement as HTMLDivElement;
          parentHolder.scrollTo({
            top: 1,
            behavior: 'smooth'
          });

          PostScheduleHelpers.scrollToPost(tableElem, rowIndex);
        }, 1);
      }
    }
  }, [posts, loadingInitial, startDate, endDate]);

  const onScroll: EventListener = useCallback((e: Event) => {
    const parent = e.currentTarget as HTMLDivElement;
    const parentOffset = parent.getBoundingClientRect();
    const rows = parent.querySelectorAll('tbody tr') as NodeListOf<HTMLTableRowElement>;
    if (postPositionsRef.current.last > -1) {
      const lastRow = rows.item(postPositionsRef.current.last);
      const lastPostOffset = lastRow.querySelector('td:last-child')?.lastElementChild?.getBoundingClientRect();
      const halfHeightLastPost = lastPostOffset ? lastPostOffset?.height / 2 : 0;

      if (
        postPositionsRef.current.post &&
        lastPostOffset &&
        lastPostOffset.top + halfHeightLastPost > parentOffset.bottom
      ) {
        setShowMore(true);
      } else {
        setShowMore(false);
      }
    }
  }, []);

  return (
    <PostScheduleWeekProvider {...providerProps}>
      <FluidContentLayoutView loading={loadingInitial}>
        <div className={leftColumnWrapperClass}>
          <div className={leftColumnClass} ref={leftColRef}>
            <PostScheduleFilterMenu
              filters={props.filters}
              context={props.context}
              dispatchFilters={props.dispatchFilters}
              categories={props.categories}
              socialProfiles={props.socialProfiles}
              formInstance={props.formInstance}
              onSubmit={props.onSubmit}
              setSearchTerm={props.setSearchTerm}
              queueCounts={queueCounts}
            />
          </div>
        </div>
        <div className={rightColumnWrapperClass}>
          <Calendar
            onScroll={onScroll}
            renderCallback={renderCallback}
            fixedHeader={true}
            data={posts}
            headerNames={headerNames}
            cellTemplate={CalendarCellTemplate}
            timeFormat={timeFormat}
            startDayOfWeek={props.dayStartOfWeek}
          />
        </div>
      </FluidContentLayoutView>
      <CSSTransition in={showMore} timeout={300} unmountOnExit classNames={'fade'} nodeRef={showMoreRef}>
        <div className={classes.ShowMore} ref={showMoreRef}>
          There are more visible posts
        </div>
      </CSSTransition>
      {queueModals.details.state && (
        <DetailedStatsModal
          posts={props.postIterations}
          onClose={() => queueModals.details.setter(false)}
          getActionMenuListItems={props.getActionMenuListItems}
          reschedulePostHandler={props.duplicateOrReschedulePost}
        />
      )}
    </PostScheduleWeekProvider>
  );
};
